import React from 'react'
import { useIntl } from 'react-intl'
import { LoanList } from '@matech/thebigpos-sdk'

import useUser from '../../../../hooks/useUser'
import formatLoanAmount from '../utils/formatLoanAmount'
import formatRoleSelector from '../utils/formatRoleSelector'
import formatBorrowerAddress from '../utils/formatBorrowerAddress'
import RowExpandedProperty from '../../../../components/RowExpandedProperty'
import extractBorrowerAndCoBorrowerFromLoansAndContacts from '../utils/extract-borrower-and-coborrower-from-loans-and-contacts'

type PipelineExpandedRowProps = {
	data: LoanList
}

const PipelineExpandedRow = ({ data }: PipelineExpandedRowProps) => {
	const { user } = useUser()
	const { formatMessage } = useIntl()

	const {
		totalLoanAmount,
		loanNumber,
		status,
		buyerAgentContact,
		sellerAgentContact,
		loanOfficer,
		propertyAddress,
		userLoans,
		contacts,
	} = data

	const { borrower, coBorrower } =
		extractBorrowerAndCoBorrowerFromLoansAndContacts(
			userLoans,
			contacts
		)

	const formattedLoanAmountValue = formatLoanAmount(totalLoanAmount)

	const formattedRole = formatRoleSelector(
		user.email,
		sellerAgentContact?.email,
		buyerAgentContact?.email
	)

	const formattedAddress = formatBorrowerAddress(propertyAddress)

	const properties = [
		{
			labelKey: 'pipeline.expanded_row.borrower',
			value: borrower?.name,
		},
		{
			labelKey: 'pipeline.expanded_row.co_borrower',
			value: coBorrower?.name,
		},
		{
			labelKey: 'global.status',
			value: status,
		},
		{
			labelKey: 'global.role',
			value: formattedRole,
		},
		{
			labelKey: 'pipeline.columns.loan_amount',
			value: formattedLoanAmountValue,
		},
		{
			labelKey: 'pipeline.columns.loan_number',
			value: loanNumber,
		},
		{
			labelKey: 'entity_type.loan_officer',
			value: loanOfficer?.name,
		},
		{
			labelKey: 'pipeline.columns.address',
			value: (
				<div className="min-w-0">
					{formattedAddress.map((line) => (
						<p
							key={line}
							className="truncate whitespace-normal font-rubik text-slate-800 font-bold mb-3 text-ellipsis overflow-hidden dark:text-white"
						>
							{line}
						</p>
					))}
				</div>
			),
		},
	]

	return (
		<div className="p-5 dark:bg-[#121212]">
			{properties.map((property) => {
				const label = formatMessage({ id: property.labelKey })
				const rowKey = label.toLowerCase().replace(' ', '-')

				return (
					<RowExpandedProperty
						key={rowKey}
						label={label}
						value={property.value}
					/>
				)
			})}
		</div>
	)
}

export default PipelineExpandedRow
